import { Component, OnInit, ViewChild, ChangeDetectorRef, SecurityContext, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { IdTagService } from 'src/app/shared/services/id-tag.service';
import { PagedResult } from 'src/app/shared/models/paging';
import { BehaviorSubject, debounceTime, distinctUntilChanged, startWith, Subject, tap, firstValueFrom } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { PageEvent } from '@angular/material/paginator';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTag, IdTagSearchEvent } from 'src/app/features/id-tags/id-tag-model';
import { DepartmentSearchEvent, Department } from 'src/app/features/department/department.model';
import { LoadingSpinnerComponent } from '../../../core/loading-spinner/loading-spinner.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatBadgeModule } from '@angular/material/badge';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';

@Component({
  selector: 'adm-id-tag-selector',
  templateUrl: './id-tag-selector.component.html',
  styleUrls: ['./id-tag-selector.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatBadgeModule,
    MatListModule,
    MatChipsModule,
    CustomDropDownArrowDirective,
    LoadingSpinnerComponent,
  ],
})
export class IdTagSelectorComponent implements OnInit {
  protected changeDetectorRef = inject(ChangeDetectorRef);
  dialogRef = inject<MatDialogRef<IdTagSelectorComponent>>(MatDialogRef);
  data = inject<{
    selectedIdTags: IdTag[];
    organizationId: number;
    organizationName: string;
    departmentId?: number;
  }>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private sanitizer = inject(DomSanitizer);
  private departmentService = inject(DepartmentService);

  @ViewChild('idTagList') idTagList: any = [];

  public idTags: Subject<PagedResult<IdTag>> = new Subject<PagedResult<IdTag>>();
  public idTagItems: IdTag[] = [];
  public selectedIdTags: IdTag[] = [];
  idTagsCheckboxes = new FormControl();

  public idTagSearchEvent: IdTagSearchEvent = {} as IdTagSearchEvent;
  public departmentSearchEvent: DepartmentSearchEvent = {} as DepartmentSearchEvent;

  public filterModel: FormGroup = this.initForm();
  public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public selectedStatus: IdTagStatusEnum | null = null;
  public departments: Department[] = [];
  public sortDirection: string = 'asc';
  public idTagStatusEnum: typeof IdTagStatusEnum = IdTagStatusEnum;
  public statuses: IdTagStatusEnum[] = [
    IdTagStatusEnum.Active,
    IdTagStatusEnum.Inactive,
    IdTagStatusEnum.NoIdTagGroup,
    IdTagStatusEnum.Blocked,
  ];

  get selectedDepartmentId() {
    return this.filterModel.get('selectedDepartmentId');
  }

  ngOnInit(): void {
    this.getDepartments();

    this.idTagSearchEvent.query = this.filterModel.controls['query'].value;

    this.selectedIdTags = this.data?.selectedIdTags.filter(l => l.organizationName === this.data.organizationName);

    if (this.data.departmentId) {
      this.selectedDepartmentId?.disable();
    }
    this.idTagsCheckboxes.valueChanges.subscribe((values: IdTag[]) => {
      values.forEach(value => {
        value.checked = true;
        if (this.selectedIdTags.findIndex(v => v.id === value.id) === -1) {
          this.selectedIdTags.push(value);
        }
      });
    });

    this.filterModel.valueChanges
      .pipe(
        startWith(''), // let one blank through to search for all
        debounceTime(100),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(val => {
        let query: string | undefined;
        let selectedStatus: IdTagStatusEnum | undefined;
        let selectedDepartmentId = this.data.departmentId;
        if (val) {
          query = val.query ?? undefined;
          selectedStatus = val.selectedStatus ?? undefined;
          if (val.selectedDepartmentId) {
            selectedDepartmentId = val.selectedDepartmentId;
          }
        }

        this.idTagSearchEvent.query = query;
        this.idTagSearchEvent.status = selectedStatus;
        this.idTagSearchEvent.departmentId = selectedDepartmentId;
        this.idTagSearchEvent.organizationId = this.data.organizationId;
        const pageEvent = new PageEvent();
        pageEvent.pageSize = 1000;
        this.idTagSearchEvent.paging = pageEvent;

        this.idTagSearchEvent.sorting = {
          active: val.sortBy,
          direction: val.sortDirection,
        };

        this.loading.next(true);

        this.idTagService.getIdTags(this.idTagSearchEvent).subscribe({
          next: result => {
            this.idTags.next(result);
            this.idTagItems = result.items.filter(i => i.status.toString() !== IdTagStatusEnum[IdTagStatusEnum.Pending]);
            this.selectedIdTags.forEach(idTag => {
              const currentIdTag = this.idTagItems.find(id => id.id === idTag.id);
              if (currentIdTag) {
                currentIdTag.checked = true;
              }
            });
            this.loading.next(false);
            this.changeDetectorRef.markForCheck();
          },
          error: () => {
            this.loading.next(false);
            this.changeDetectorRef.markForCheck();
          },
        });

        //due to ExpressionChangedAfterItHasBeenCheckedError when populating matBadge counter
        this.changeDetectorRef.detectChanges();
      });
  }

  public idTagOptionWasClicked(idTagId: number) {
    const indexOption = (this.idTagsCheckboxes.value as IdTag[]).findIndex(v => v.id === idTagId);
    const indexIdTag = this.selectedIdTags.findIndex(v => v.id === idTagId);

    if (indexOption === -1) {
      this.selectedIdTags.splice(indexIdTag, 1);
    }

    if (this.selectedIdTags.length === 0) {
      this.filterModel.controls['selectedDepartmentId'].setValue(null);
    }
  }

  public resetIdTags() {
    this.selectedIdTags = [];
    this.idTagsCheckboxes.setValue(this.selectedIdTags);
  }

  public loadSelectedIdTags() {
    if (this.selectedIdTags.length > 0) {
      this.idTagItems = this.selectedIdTags;
    }
  }

  public sortChange(sortBy: string) {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.filterModel.patchValue({
      sortBy: sortBy,
      sortDirection: this.sortDirection,
    });
  }

  saveAndClose() {
    this.dialogRef.close(this.selectedIdTags);
  }

  close() {
    this.dialogRef.close(false);
  }

  private initForm() {
    return new FormGroup({
      query: new FormControl(''),
      selectedStatus: new FormControl<IdTagStatusEnum | null>(null),
      selectedDepartmentId: new FormControl<number | null>(this.data.departmentId ?? null),
      sortBy: new FormControl(''),
      sortDirection: new FormControl(this.sortDirection),
    });
  }

  private async getDepartments() {
    this.departmentSearchEvent.organizationId = this.data.organizationId;
    const pagedDepartments = await firstValueFrom(this.departmentService.getDepartments(this.departmentSearchEvent));
    this.departments = pagedDepartments.items;
  }
}
